import { useMemo, useRef, useCallback, useEffect } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import { yaml } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { keymap } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import { acceptCompletion } from '@codemirror/autocomplete'
import { search, searchKeymap, selectSelectionMatches } from '@codemirror/search'
import { indentWithTab } from '@codemirror/commands'
import { semanticAutocomplete } from '../autocomplete-semantic'
import {
  setChangeMarkers,
  setDeleteDecos,
  changeMarkerField,
  deleteDecoField,
  changeGutter,
  computeChangeMarkers,
} from '../diff-gutter'

export default function YamlEditor({ value, onChange, placeholder, originalValue, editorTheme }) {
  const viewRef = useRef(null)
  const originalLines = useRef([])

  // originalValue가 변경되면 (생성/로드/저장 시) 기준점 갱신 + 마커 재계산
  useEffect(() => {
    if (originalValue != null) {
      originalLines.current = originalValue.split('\n')
    }
    const view = viewRef.current
    if (view && originalLines.current.length > 0) {
      const result = computeChangeMarkers(view.state.doc, originalLines.current)
      view.dispatch({ effects: [
        setChangeMarkers.of(result.gutterMarkers),
        setDeleteDecos.of(result.deleteDecos),
      ] })
    }
  }, [originalValue])

  const extensions = useMemo(() => [
    yaml(),
    search({ top: true }),
    semanticAutocomplete(),
    Prec.highest(keymap.of([
      ...searchKeymap,
      { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
    ])),
    keymap.of([
      { key: 'Tab', run: acceptCompletion },
      indentWithTab,
    ]),
    changeMarkerField,
    deleteDecoField,
    changeGutter,
  ], [])

  const handleChange = useCallback((val, viewUpdate) => {
    onChange?.(val)
    const view = viewUpdate.view
    if (originalLines.current.length > 0) {
      const result = computeChangeMarkers(view.state.doc, originalLines.current)
      view.dispatch({ effects: [
        setChangeMarkers.of(result.gutterMarkers),
        setDeleteDecos.of(result.deleteDecos),
      ] })
    }
  }, [onChange])

  const handleCreateEditor = useCallback((view) => {
    viewRef.current = view
    if (originalLines.current.length > 0) {
      const result = computeChangeMarkers(view.state.doc, originalLines.current)
      view.dispatch({ effects: [
        setChangeMarkers.of(result.gutterMarkers),
        setDeleteDecos.of(result.deleteDecos),
      ] })
    }
  }, [])

  return (
    <div className="editor-wrap">
      {value ? (
        <CodeMirror
          value={value}
          height="100%"
          theme={editorTheme || oneDark}
          extensions={extensions}
          onChange={handleChange}
          onCreateEditor={handleCreateEditor}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            autocompletion: false,
          }}
        />
      ) : (
        <div className="placeholder">{placeholder}</div>
      )}
    </div>
  )
}
